// cellRenderers.js
import React from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { t } from 'i18next';
import InfoPopup from '../InfoPopup';
import { capitalizeFirstLetter, formatNumber, formatNumberWithThreeDigitPrecision, formatNumberWithoutFractions, getStepPercent } from '~/utils/helpers';
import { RouteTypes } from '~/routes/constants';
import { Icon, Label, Popup } from 'semantic-ui-react';
import ENV from '~/environment';
import CustomIcon from '../CustomIcon';
import { StyledCellValue } from '~/styles/table';
import { StyledLink } from './style';
import { Dot } from '../DotWithLabel';

function terminationDeliveryDate(row, firstColor) {
  const { terminationDate, lastDeliveryDate } = row
  return <>
    {terminationDate && <Popup
      content={t('Cancellation date: {{date}}', { date: dayjs(terminationDate).format('DD.MM.YYYY') })}
      trigger={<Icon style={{ color: firstColor, fontSize: '16px' }} name={'delete calendar'} />}
    />
    }
    {lastDeliveryDate && <Popup
      content={t('Date of last delivery: {{date}}', { date: dayjs(lastDeliveryDate).format('DD.MM.YYYY') })}
      trigger={<Icon style={{ color: firstColor, fontSize: '16px' }} name={'checked calendar'} />}
    />
    }
  </>
}

function renderDate(start, end) {
  const dateFrom = new Date(start);
  const dateTo = new Date(end);
  // get Day name and year
  const dateWithDayAndYearTo = capitalizeFirstLetter(dayjs(dateTo).format('dddd, D MMMM YYYY HH:mm'));
  const dateWithDayAndYear = capitalizeFirstLetter(dayjs(dateFrom).format('dddd, D MMMM YYYY HH:mm'));

  return <>
    <StyledCellValue>{`${dateWithDayAndYear}`}</StyledCellValue>
    <StyledCellValue>{`${dateWithDayAndYearTo}`}</StyledCellValue>
  </>;
}

function getTotalFarmers(row) {
  const { offeredFarmers, totalFarmers } = row
  //@ts-ignore
  return `${offeredFarmers} / ${totalFarmers}  (${formatNumber(totalFarmers === 0 ? 0 : offeredFarmers / totalFarmers * 100)}%) `
}

const renderTaxNumber = ({ value }) => {
  // Renders a tax number with a percentage sign
  return value ? `${formatNumber(parseFloat(value))} %` : `${formatNumber(0)} %`;
};

const renderBillingMonth = ({ value }) => {
  // Renders a billing month in the format "MMM YYYY"
  return dayjs(value).format('MMM YYYY');
};

const renderNoUnitNumberNoValue = ({ value }) => {
  // Renders a number without a unit, or an empty string if the value is falsy
  return value ? `${formatNumber(parseFloat(value), 0, '-')}` : '';
};

const renderTradingSlotLink = ({ value, row }) => {
  // Renders a link to the trading slot show page
  return <Link to={RouteTypes.MILK_FIXED_PRICE_SHOW.replace(':id', row?.tradingSlotId)}> {value}</Link>;
};

const renderConvertNumberToMonth = ({ value, row }) => {
  // Converts a number to a month and year in the format "MMM YYYY"
  return dayjs(`${row?.tradingYear}-${value}-01`).format('MMM YYYY');
};

const renderLattestComment = ({ value }) => {
  // Renders the latest comment with an info popup
  return value ? <InfoPopup content={value} size='large' styleIcon={{ fontSize: '1.4em' }} /> : t('No comments');
};

const renderSurveyParticipation = ({ row }) => {
  // Renders survey participation statistics
  return `${row?.completedFarmers} / ${row.totalFarmers}
    (${row?.totalFarmers === 0 ? '0,00' : formatNumber(row?.completedFarmers / row?.totalFarmers * 100)} %)`;
};

const renderFarmerLink = ({ value, row }) => {
  // Renders a link to the farmer show page
  return <Link to={RouteTypes.DAIRY_FACTORY_FARMERS_SHOW.replace(':id', row.id)} style={{ color: '#4183c4' }}>{value}</Link>;
};

const renderSurveyStatus = ({ value }) => {
  // Renders the survey status as "Completed" or "Not completed"
  return value ? t('Completed') : t('Not completed');
};

const renderSupplierLink = ({ value, row }) => {
  // Renders a link to the supplier or a regular anchor tag
  return row?.companyName ? (
    <Popup
      content={row?.companyName}
      trigger={
        <Link
          to={row?.farmerId ? `${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW.replace(':id', row?.farmerId)}` : ''}
          rel='noreferrer'
        >
          {value}
        </Link>
      }
    />
  ) : (
    <a
      target={row?.farmerId ? '_blank' : ''}
      onClick={(e) => {
        if (!row?.farmerId) {
          e.preventDefault();
        }
      }}
      href={row?.farmerId ? `${ENV}/${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW.replace(':id', row?.farmerId)}` : ''}
      rel='noreferrer'
    >
      {value}
    </a>
  );
};

const renderPhValue = ({ value }) => {
  // Renders the pH value or "Not defined"
  return !value ? (value == 0 ? 0 : t('Not defined')) : value;
};

const renderCalendar = ({ value, row, firstColor, column }) => {
  // Renders the calendar value and termination/delivery date if available
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
      {column.link ? <Link style={{ color: '#4183c4' }} to={column.link.replace(':id', row.id)}>{value}</Link> : <div>{value}</div>}
      {(row.terminationDate || row.lastDeliveryDate) && terminationDeliveryDate(row, firstColor)}
    </div>
  );
};

const renderRemainingFarmer = ({ row, amountUnit }) => {
  // Renders the remaining farmer amount and percentage

  const confirmedAmountRemaining = row.confirmedAmount ? row.confirmedAmount : 0
  const remainingAmount = formatNumberWithoutFractions(row.yearlyAmount - confirmedAmountRemaining);
  return row.yearlyAmount - confirmedAmountRemaining > 0
    ? `(${remainingAmount} ${amountUnit}) / 
        [${formatNumberWithThreeDigitPrecision(100 * Number(row.yearlyAmount - confirmedAmountRemaining) / Number(row.yearlyAmount))} %]`
    : `(0 ${amountUnit}) / [0,00 %]`;
};

const renderPercent = ({ value, row }) => {
  // Renders a percentage value
  return `${value}/${row.totalRecipients} (${row.totalRecipients ? formatNumberWithoutFractions(value / row.totalRecipients * 100) : 0} %)`;
};

const renderOfferedAmount = ({ value, row, amountUnit }) => {
  // Renders the offered amount and percentage
  return `[${formatNumberWithoutFractions(value)} ${amountUnit} / 
        ${row.maxAmount ? formatNumber(value / row.maxAmount * 100) : 0} %]`;
};

const renderRemainingAmount = ({ value, row, amountUnit }) => {
  // Renders the remaining amount and percentage
  return `[${formatNumberWithoutFractions(value)} ${amountUnit} / ${row.maxAmount ? formatNumber(value / row.maxAmount * 100) : 0} %]`;
};

const renderExpirationDate = ({ row }) => {
  // Renders the expiration date
  const date = row?.year ? new Date(row?.year, row?.month - 1) : new Date(row?.expiryYear, row?.expiryMonth - 1);
  return capitalizeFirstLetter(dayjs(date).format('MMM YYYY'));
};

const renderCommodity = ({ value, options }) => {
  // Renders the commodity text based on the value
  const commodity = options?.find(symbol => symbol.value === value);
  return commodity?.text;
};

const renderNetChange = ({ value }) => {
  // Renders the net change with a color based on the value
  return <div style={{ color: parseFloat(value) > 0 ? 'green' : parseFloat(value) === 0 ? 'black' : 'red' }}>{formatNumber(value)}</div>;
};

const renderBrowser = ({ row }) => {
  // Renders the browser name and version
  return `${row.browserName || ''} ${row.browserVersion || ''}`;
};

const renderDevice = ({ row }) => {
  // Renders the device, platform name, and platform version
  return `${row.device || ''} ${row.platformName || ''} ${row.platformVersion || ''}`;
};

const renderNewsletterSent = ({ value }) => {
  // Renders a label for newsletter sent status
  return value === 1 ? <Label>{t('Read')}</Label> : <Label>{t('New')}</Label>;
};

const renderConfirmFatProtein = ({ row }) => {
  // Renders the confirmed fat and protein amount and percentage
  const amountConfirmed = row.offeredAmount ? row.offeredAmount : row.amount
  return row.confirmedAmount
    ? `0 kg [${formatNumberWithoutFractions(row.confirmedAmount)} kg] /
          0 % [${formatNumber(row.confirmedAmount / amountConfirmed * 100)} %]`
    : 0;
};

const renderConfirm = ({ row, amountUnit }) => {
  // Renders the confirmed amount and percentage
  const amountConfirmed = row.offeredAmount ? row.offeredAmount : row.amount
  return row.confirmedAmount ? `0 ${amountUnit} [${formatNumberWithoutFractions(row.confirmedAmount)} ${amountUnit}] /
  0 % [${formatNumber(row.confirmedAmount / amountConfirmed * 100)} %]`
    : 0;
};

const renderConfirmWithPercent = ({ row, amountUnit }) => {
  // Renders the confirmed amount and percentage
  return row.confirmedAmount
    ? `(${formatNumberWithoutFractions(row.confirmedAmount)} ${amountUnit}) / 
[${formatNumber(row.confirmedAmount / row?.yearlyAmount * 100)} %]`
    : `(0 ${amountUnit}) / [0,00 %]`;
};

const renderMobileNumber = ({ row }) => {
  // Renders the mobile number with country code
  return row.mobileNumber ? row.mobileNumber : '';
};

const renderAddress = ({ row }) => {
  // Renders the address
  return (
    <div>
      <div>{`${row.streetName} ${row.streetNumber}.`}</div>
      <div>{`${row.zip} ${row.city}.`}</div>
      <div>{`${row.countryLabel} `}</div>
    </div>
  );
};

const renderStatus = ({ value }) => {
  // Renders the status as "Active" or "Inactive"
  return value === 1 ? t('Active') : t('Inactive');
};

const renderVerified = ({ value, row }) => {
  // Renders a checkmark or close icon based on the value, and a popup with the opened date if available
  return !value ? (
    <CustomIcon color='red' name='close' size='large' />
  ) : row.openedInAppAt ? (
    <Popup
      trigger={<CustomIcon color='green' name='checkmark' size='large' />}
      content={dayjs(row.openedInAppAt).format('DD.MM.YYYY')}
    />
  ) : (
    <CustomIcon color='green' name='checkmark' size='large' />
  );
};

const renderPrice = ({ row }) => {
  // Renders the price or 0 if the status is "in_progress"
  const { confirmedAmount, amount, status } = row
  return status === 'in_progress' ? 0 : `[${formatNumberWithoutFractions(confirmedAmount)} / ${!amount ? '0,00' : formatNumber(confirmedAmount / amount)} %]`;
};

const renderAvailable = ({ value, row, column, amountUnit }) => {
  // Renders the available amount and percentage
  const { amount } = row
  return `(${formatNumberWithoutFractions(value)} ${column?.amountUnit || amountUnit}) / [${!amount ? '0,00' : formatNumber(value / amount * 100)} %]`;
};

const renderConfirmed = ({ row, column, amountUnit }) => {
  // Renders the confirmed amount and percentage
  const { confirmedAmount, amount } = row
  return `(${formatNumberWithoutFractions(confirmedAmount)} ${column?.amountUnit || amountUnit}) / [${!amount ? '0,00' : formatNumber(confirmedAmount / amount * 100)} %]`;
};

const renderRibon = ({ value }) => {
  // Renders a label with a ribbon
  return <Label ribbon>{value}</Label>;
};

const renderDateRibbon = ({ value, column }) => {
  // Renders a date ribbon or "Not defined" if the value is falsy
  const { format } = column
  return <Label ribbon>{value ? capitalizeFirstLetter(dayjs(value).format(format)) : t('Not defined')}</Label>;
};

const renderNumberWhole = ({ value, amountUnit }) => {
  // Renders a whole number with a unit
  return value ? `${formatNumberWithoutFractions(parseInt(value))} ${amountUnit}` : `0 ${amountUnit}`;
};

const renderNumber = ({ value, amountUnit, column }) => {
  // Renders a number with two decimal places and a unit
  const unit = column?.amountUnit || amountUnit
  return value ? `${formatNumberWithoutFractions(parseFloat(value).toFixed(2))} ${unit}` : `0 ${unit}`;
};

const renderPriceTotal = ({ value }) => {
  // Renders a price with a currency symbol
  return value ? `${formatNumber(parseFloat(value))} €` : '0,00 €';
};

const renderNoUnitNumber = ({ value, column }) => {
  // Renders a number without a unit
  return value ? `${formatNumber(parseFloat(value), column?.decimalScale)}` : `${formatNumber(0, column?.decimalScale)}`;
};

const renderOrNumber = ({ value, column }) => {
  // Renders a number without a unit
  return value ? `${formatNumber(parseFloat(value), column?.decimalScale)} ${column?.unit ? `(${column?.unit})` : ''}` : '';
};

const renderWithUnit = ({ value, column }) => {
  // Renders a number without a unit
  if (column?.decimalScale) {
    return `${formatNumber(value, column.decimalScale)} ${column.unit}`
  }
  return `${value} ${column.unit}`
};

const renderLabel = ({ value }) => {
  // Renders a label with a ribbon
  return <Label ribbon>{value}</Label>;
};

const renderSurveyParticipants = ({ row }) => {
  // Renders survey participants statistics
  return `${row?.completedFarmers} / ${row?.totalFarmers} (${row?.totalFarmers === 0 ? '0,00' : formatNumber(row?.completedFarmers / row?.totalFarmers * 100)} %)`;
};

const renderTotalFarmers = ({ row }) => {
  // Renders the total number of farmers
  return getTotalFarmers(row);
};

const renderStartEnd = ({ row }) => {
  // Renders the start and end dates
  return renderDate(row.dateFrom, row.dateTo);
};

const renderStartEndSurvey = ({ row }) => {
  // Renders the start and end dates for a survey
  return renderDate(row.start, row.end);
};

const renderMin = ({ options, amountUnit }) => {
  // Renders the minimum contingent with a unit
  return `${formatNumberWithoutFractions(options?.minimumContingent)} ${amountUnit}`;
};

const renderDateFormat = ({ value, column }) => {
  // Renders a date or a missing result message if the value is falsy
  const { format, missingResultMessage } = column
  return value ? dayjs(value).format(format) : missingResultMessage || '';
};

const renderDefault = ({ value }) => {
  // Renders the default value as is
  return value;
};

const renderWithComponent = ({ column, value, row, renderColumn, index }) => {
  return renderColumn(column.type, value, row, index)
}

const renderNewsletter = ({ column, value, row, renderColumn }) => {
  return renderColumn(column.type, value, row)
}

const renderValueWithUnit = ({ column, value }) => {
  return `${formatNumber(value, column?.decimalScale || 2)} ${column?.amountUnit || ''}`
}

const renderPeriodClimate = ({ row }) => {
  const dateFrom = new Date(row.periodFrom);
  const dateTo = new Date(row.periodTo);
  // get Day name and year
  const dateWithDayAndYearTo = capitalizeFirstLetter(dayjs(dateTo).format('dddd, D MMMM YYYY'));
  const dateWithDayAndYear = capitalizeFirstLetter(dayjs(dateFrom).format('dddd, D MMMM YYYY'));

  return <>
    <StyledCellValue>{`${dateWithDayAndYear}`}</StyledCellValue>
    <StyledCellValue>{`${dateWithDayAndYearTo}`}</StyledCellValue>
  </>;
}

const renderFarmerGroups = ({ value }) => {
  return <div>
    {value.map((farmerGroup, index) => <StyledLink
      key={farmerGroup.id}
      to={RouteTypes.DAIRY_FACTORY_PROFILE_FARMER_GROUPS_SHOW.replace(':id', farmerGroup.id)}
    >
      {`${farmerGroup.name}${index === value.length - 1 ? ' ' : ', '}`}
    </StyledLink>
    )}
  </div>
}

const renderFarmerGroupsText = ({ value }) => {
  return <div>
    {value.map((farmerGroup, index) => <span
      key={farmerGroup.id}
    >
      {`${farmerGroup.name}${index === value.length - 1 ? ' ' : ', '}`}
    </span>
    )}
  </div>
}

const renderCo2Progress = ({ row }) => {
  return row?.completed === 1 ? '100 %' : `${getStepPercent(parseInt(row?.step))} %`
}

const renderClimaProgress = ({ row, value }) => {
  return row?.completed === 1 ? '100 %' : `${value} %`
}

const renderPriceChange = ({ column, value, row, renderColumn }) => {
  return renderColumn(column.type, value, row)
}

const renderColorCell = ({ value }) => {
  return <Dot color={value} />
}

const renderBlePrice = ({ value, row }) => {
  return row?.initialPrice ? <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
    <div>{formatNumber(value, 2)}</div>
    <InfoPopup
      content={t('Original BLE price: {{value}}', { value: formatNumber(row?.initialPrice, 2) })}
      styleIcon={{ cursor: 'pointer', fontSize: '1.2em' }}
      position={'top center'}
    />
  </div> : <div>{formatNumber(value, 2)}</div>
}

const renderBlePriceResults = ({ row }) => {
  return row?.initialPrice ? `${formatNumber(row?.price, 2)} [${formatNumber(row.initialPrice, 2)}]` : `${formatNumber(row?.price, 2)}`
}

const renderInvoiceErrors = ({ value, row }) => {
  if (value) {
    return <Popup
      trigger={<CustomIcon color='red' name='cancel' size='large' />}
      content={
        <div>
          {row.errors?.map((error, index) => (
            <div key={error.id} style={{ fontStyle: 'italic', color: 'red' }}>{`${index + 1}. ${error?.label}!`}</div>
          ))}
        </div>
      }
    />
  }
  return <CustomIcon color='green' name='checkmark' size='large' />
}

const renderLink = ({ value, row, column }) => {
  return <Link style={{ color: '#4183c4' }} to={column.link.replace(':id', row.id)}>{value}</Link>
}

const renderNoteCreator = ({ value, row }) => {
  return <div>{row?.creator?.name || value}</div>
}

const renderNoteContent = ({ value }) => {
  return <div
    style={{
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }}>
    {value}
  </div>
}

const renderStreet = ({ row }) => {
  // Renders the address
  return (
    <div>{`${row.streetName} ${row.streetNumber}.`}</div>
  );
};

const renderDangerouesHtml = (tableValues) => {
  return (
    <div>{renderNoUnitNumber({ ...tableValues })}</div>
  );
};

const renderAgolineCowPerDay = ({ row }) => {
  const agolinePerDay = formatNumber(parseFloat(row.agolineContent) * row.dailyMineralIntakePerCow / 100, 2)
  return <div>{agolinePerDay}</div>
}

const renderManagersFarmers = ({ value }) => {
  const farmers = value || []
  if (!farmers.length) {
    return <Label>{t('No farmers assigned')}</Label>
  }
  return (
    <Popup
      trigger={
        <Label style={{ cursor: 'pointer' }}>
          {`${farmers.length} ${t('Farmers')}`}
        </Label>
      }
      content={
        <div>
          {farmers.map((farmer) => (
            <div key={farmer.farmerId} style={{ marginBottom: '4px' }}>
              <Link to={RouteTypes.DAIRY_FACTORY_FARMERS_SHOW.replace(':id', farmer.farmerId)} style={{ color: '#4183c4' }}>
                {`${farmer.agisId} - ${farmer.companyName}`}
              </Link>
            </div>
          ))}
        </div>
      }
      on='hover'
    />
  )
}

const renderDateBirth = ({ value, column }) => {
  const format = column?.format || 'DD.MM.YYYY'
  const age = value ? dayjs().diff(dayjs(value), 'year') : null
  return age ? `${dayjs(value).format(format)} (${age})` : ''
}

const renderPeriods = ({ row }) => {
  const dateFrom = new Date(row.periodFrom);
  const dateTo = new Date(row.periodTo);
  // get Day name and year
  const dateWithDayAndYearTo = capitalizeFirstLetter(dayjs(dateTo).format('DD.MM.YYYY'));
  const dateWithDayAndYear = capitalizeFirstLetter(dayjs(dateFrom).format('DD.MM.YYYY'));

  return <>
    <StyledCellValue>{`${dateWithDayAndYear} - ${dateWithDayAndYearTo}`}</StyledCellValue>
  </>;
}

const renderCreatorName = ({ row }) => {

  return <>
    <StyledCellValue>{row?.creator?.name}</StyledCellValue>
  </>;
}

export const cellRendererMapping = {
  'taxNumber': renderTaxNumber,
  'billingMonth': renderBillingMonth,
  'no-unit-number-no-value': renderNoUnitNumberNoValue,
  'tradingSlotLink': renderTradingSlotLink,
  'convertNumberToMonth': renderConvertNumberToMonth,
  'lattestComment': renderLattestComment,
  'surveyParticipation': renderSurveyParticipation,
  'farmerLink': renderFarmerLink,
  'surveyStatus': renderSurveyStatus,
  'supplier-link': renderSupplierLink,
  'phValue': renderPhValue,
  'calendar': renderCalendar,
  'remaining-farmer': renderRemainingFarmer,
  'percent': renderPercent,
  'offered-amount': renderOfferedAmount,
  'remaining-amount': renderRemainingAmount,
  'expiration-date': renderExpirationDate,
  'commodity': renderCommodity,
  'netChange': renderNetChange,
  'browser': renderBrowser,
  'device': renderDevice,
  'newsletter-sent': renderNewsletterSent,
  'confirm-fat-protein': renderConfirmFatProtein,
  'confirm': renderConfirm,
  'confirmWithPercent': renderConfirmWithPercent,
  'mobileNumber': renderMobileNumber,
  'address': renderAddress,
  'status': renderStatus,
  'verified': renderVerified,
  'price': renderPrice,
  'available': renderAvailable,
  'confirmed': renderConfirmed,
  'ribon': renderRibon,
  'dateRibbon': renderDateRibbon,
  'number-whole': renderNumberWhole,
  'number': renderNumber,
  'price-total': renderPriceTotal,
  'no-unit-number': renderNoUnitNumber,
  'label': renderLabel,
  'survey-participants': renderSurveyParticipants,
  'totalFarmers': renderTotalFarmers,
  'startEnd': renderStartEnd,
  'startEndSurvey': renderStartEndSurvey,
  'min': renderMin,
  'date': renderDateFormat,
  'default': renderDefault,
  'changes': renderWithComponent,
  'percent-status-news': renderWithComponent,
  'delete-offer': renderWithComponent,
  'email-status': renderWithComponent,
  'email-type': renderWithComponent,
  'newsletter': renderNewsletter,
  'newsletter-type': renderWithComponent,
  'input-confirm': renderWithComponent,
  'input': renderWithComponent,
  'answerColumn': renderWithComponent,
  'climate-check-period': renderPeriodClimate,
  'value-with-unit': renderValueWithUnit,
  'farmer-groups': renderFarmerGroups,
  'co2-progress': renderCo2Progress,
  'price-change': renderPriceChange,
  'color-label': renderColorCell,
  'ble-price': renderBlePrice,
  'number-or-value': renderOrNumber,
  'ble-price-change': renderBlePriceResults,
  'invoice-errors': renderInvoiceErrors,
  'render-with-unit': renderWithUnit,
  'link': renderLink,
  'note-creator': renderNoteCreator,
  'note-content': renderNoteContent,
  'clima-progress': renderClimaProgress,
  'farmer-groups-text': renderFarmerGroupsText,
  'street': renderStreet,
  'agolin-per-cow-per-day': renderAgolineCowPerDay,
  'html-header': renderDangerouesHtml,
  'manager-farmers': renderManagersFarmers,
  'date-birth': renderDateBirth,
  'periods': renderPeriods,
  'creatorName': renderCreatorName,
  'change-request-select': renderWithComponent,
  'change-request-type': renderWithComponent,
  'change-request-field': renderWithComponent,
  'change-request-action': renderWithComponent,
  'change-request-old-value': renderWithComponent,
  'change-request-new-value': renderWithComponent,
  'change-request-status': renderWithComponent
};